<template>
  <div
    class="stat-card-white"
    ref="captureArea"
    :class="{ 'chart-empty-state': !loader && !plotData.length }"
  >
    <div class="stat-header-white d-flex justify-content-between align-items-center">
      <span>{{ chartLabel }}</span>
      <div class="d-flex align-items-center">
        <div class="expand-block px-2 mb-1 cursor-pointer position-relative" @click="showtable">
          <img src="../../assets/img/table_view.png" alt="Data overview" />
          <div class="more-expand"><span>Data Overview</span></div>
        </div>
        <div class="expand-block px-2 mb-1 cursor-pointer" @click.prevent="expandBlock = true">
          <img src="../../assets/img/expand.png" alt="Expand" />
        </div>
        <div class="text-center" v-on:clickout="showPopup = false">
          <div
            class="position-relative cursor-pointer biggerfont ml-2"
            @click.prevent="showPopup = !showPopup"
          >
            <i class="fa fa-ellipsis-v"></i>
          </div>
          <div class="plan-side-popup" ref="excludeThis" v-if="showPopup">
            <div @click.prevent="showPopup = false; exportToExcel()">Export to Excel</div>
            <div @click.prevent="showPopup = false; exportToPdf()">Export to PDF</div>
          </div>
        </div>
      </div>
    </div>

    <div class="stat-body-white d-flex flex-column" :class="{ 'h-100': loader }">
      <div v-if="loader" class="loader"></div>

      <div
        v-if="!loader && selectedGroups.length"
        class="selectdrill d-flex flex-row-reverse gap-2 w-100"
      >
        <img
          class="cursor-pointer"
          src="../../assets/img/chart_cancel.png"
          @click.prevent="cancelSelection"
          alt="Cancel"
        />
        <img
          class="cursor-pointer"
          src="../../assets/img/chart_submit.png"
          @click.prevent="recreateChart"
          alt="Submit"
        />
      </div>

      <div v-show="!loader" class="chart-container position-relative" ref="chartWrap">
        <svg ref="chartSvg"></svg>
      </div>

      <div ref="tooltip" class="chart-tooltip"></div>
    </div>
  </div>

  <enlargerpopup v-if="expandBlock" @closeModal="handleExpandClose">
    <div class="popupheight d-flex flex-column col-md-12">
      <div v-if="selectedGroups.length" class="selectdrill d-flex flex-row-reverse gap-2 w-100">
        <img
          class="cursor-pointer"
          src="../../assets/img/chart_cancel.png"
          @click.prevent="cancelSelection"
          alt="Cancel"
        />
        <img
          class="cursor-pointer"
          src="../../assets/img/chart_submit.png"
          @click.prevent="recreateChart"
          alt="Submit"
        />
      </div>
      <div class="chart-container popupconatiner position-relative" ref="popupChartWrap">
        <svg ref="popupChartSvg"></svg>
      </div>
    </div>
  </enlargerpopup>
</template>

<script>
import * as d3 from 'd3';
import enlargerpopup from '../AiCharts/popupfiles/enlarge_popup.vue';
import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js';
import chartEmptyStateMixin from './chartEmptyStateMixin';

const DEFAULT_COLORS = [
  '#4472C4', '#ED7D31', '#A9D18E', '#FF0000', '#FFC000',
  '#5B9BD5', '#70AD47', '#C00000', '#7030A0', '#00B0F0',
];

export default {
  name: 'horizondalPercentageBar',

  mixins: [chartEmptyStateMixin],

  components: {
    enlargerpopup,
  },

  props: {
    dataArray: { type: Array, default: () => [] },
    groupField: { type: String, required: true },
    chartLabel: { type: String, default: '' },
    loader: { type: Boolean, default: false },
    minIncidence: { type: Number, default: 0 },
    sortOrder: { type: String, default: 'desc' },
    barColors: { type: Array, default: () => [] },
  },

  emits: ['opendrillpopup', 'drilldowncurrentchart'],

  data() {
    return {
      showPopup: false,
      expandBlock: false,
      selectedGroups: [],
      sortdata: [],
      filterarray: [],
      filterkeyword: '',
      plotData: [],
      resizeObserver: null,
      popupResizeObserver: null,
    };
  },

  computed: {
    palette() {
      return this.barColors.length ? this.barColors : DEFAULT_COLORS;
    },
  },

  watch: {
    dataArray: {
      immediate: true,
      deep: true,
      handler() {
        this.refreshChartData(true);
      },
    },
    groupField() {
      this.refreshChartData(true);
    },
    minIncidence() {
      this.refreshChartData(true);
    },
    sortOrder() {
      this.refreshChartData(true);
    },
    barColors: {
      deep: true,
      handler() {
        this.renderAllCharts();
      },
    },
    expandBlock(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.attachPopupResizeObserver();
          this.renderPopupChart();
        });
      } else {
        this.detachPopupResizeObserver();
        this.$nextTick(() => this.renderMainChart());
      }
    },
  },

  mounted() {
    this.attachResizeObserver();
    this.$nextTick(() => this.renderAllCharts());
  },

  beforeUnmount() {
    this.detachResizeObserver();
    this.detachPopupResizeObserver();
  },

  methods: {
    refreshChartData(resetSelection = false) {
      if (resetSelection) {
        this.selectedGroups = [];
        this.sortdata = [];
        this.filterarray = [];
        this.filterkeyword = '';
      }

      this.plotData = this.buildPlotData();
      this.$nextTick(() => this.renderAllCharts());
    },

    buildPlotData() {
      if (!Array.isArray(this.dataArray) || !this.dataArray.length || !this.groupField) {
        return [];
      }

      const grouped = {};

      this.dataArray.forEach((item) => {
        const raw = item?.[this.groupField];
        const key = raw != null ? String(raw).toUpperCase() : 'NULL';

        if (!grouped[key]) {
          grouped[key] = {
            name: key,
            count: 0,
            dataArray: [],
          };
        }

        grouped[key].count += 1;
        grouped[key].dataArray.push(item);
      });

      const total = this.dataArray.length || 1;
      let result = Object.values(grouped).map((entry) => ({
        name: entry.name,
        percentage: Number(((entry.count / total) * 100).toFixed(2)),
        dataArray: entry.dataArray,
      }));

      if (this.minIncidence > 0) {
        result = result.filter((entry) => entry.percentage >= this.minIncidence);
      }

      if (this.sortOrder === 'desc') {
        result.sort((a, b) => b.percentage - a.percentage);
      } else if (this.sortOrder === 'asc') {
        result.sort((a, b) => a.percentage - b.percentage);
      }

      return result;
    },

    attachResizeObserver() {
      if (!window.ResizeObserver || !this.$refs.chartWrap) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.renderMainChart();
      });

      this.resizeObserver.observe(this.$refs.chartWrap);
    },

    detachResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },

    attachPopupResizeObserver() {
      if (!window.ResizeObserver || !this.$refs.popupChartWrap) return;

      this.detachPopupResizeObserver();

      this.popupResizeObserver = new ResizeObserver(() => {
        this.renderPopupChart();
      });

      this.popupResizeObserver.observe(this.$refs.popupChartWrap);
    },

    detachPopupResizeObserver() {
      if (this.popupResizeObserver) {
        this.popupResizeObserver.disconnect();
        this.popupResizeObserver = null;
      }
    },

    renderAllCharts() {
      this.renderMainChart();

      if (this.expandBlock) {
        this.renderPopupChart();
      }
    },

    renderMainChart() {
      this.renderChart(this.$refs.chartSvg, this.$refs.chartWrap, false);
    },

    renderPopupChart() {
      this.renderChart(this.$refs.popupChartSvg, this.$refs.popupChartWrap, true);
    },

    renderChart(svgRef, wrapRef, isPopup) {
      if (!svgRef || !wrapRef) return;

      const svg = d3.select(svgRef);
      svg.selectAll('*').remove();

      if (!this.plotData.length) return;

      const width = Math.max(wrapRef.clientWidth || 0, isPopup ? 760 : 520);
      const height = Math.max(this.plotData.length * (isPopup ? 48 : 42) + 64, isPopup ? 240 : 140);
      const maxLabelLength = this.plotData.reduce((max, item) => Math.max(max, item.name.length), 0);
      const margin = {
        top: 20,
        right: 72,
        bottom: 36,
        left: Math.min(Math.max((maxLabelLength * 7) + 28, 140), isPopup ? 340 : 260),
      };
      const innerWidth = Math.max(width - margin.left - margin.right, 120);
      const innerHeight = Math.max(height - margin.top - margin.bottom, 80);

      svg
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .style('width', '100%')
        .style('height', 'auto')
        .style('display', 'block');

      const root = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleLinear()
        .domain([0, 100])
        .range([0, innerWidth]);

      const y = d3.scaleBand()
        .domain(this.plotData.map((item) => item.name))
        .range([0, innerHeight])
        .padding(0.22);

      root
        .append('g')
        .attr('class', 'grid')
        .call(
          d3.axisBottom(x)
            .ticks(5)
            .tickSize(innerHeight)
            .tickFormat(() => ''),
        )
        .call((g) => g.select('.domain').remove())
        .call((g) => g.selectAll('line').attr('stroke', '#e8e8e8').attr('stroke-dasharray', '4,4'));

      root
        .append('g')
        .attr('transform', `translate(0,${innerHeight})`)
        .call(d3.axisBottom(x).ticks(5).tickFormat((value) => `${value}%`))
        .call((g) => g.select('.domain').attr('stroke', '#d0d0d0'))
        .call((g) => g.selectAll('text').style('font-size', '11px').style('fill', '#888888'))
        .call((g) => g.selectAll('line').attr('stroke', '#d0d0d0'));

      root
        .append('g')
        .call(d3.axisLeft(y).tickSize(0))
        .call((g) => g.select('.domain').remove())
        .call((g) => g.selectAll('text')
          .style('font-size', '12px')
          .style('fill', '#444444')
          .text((label) => this.formatAxisLabel(label, isPopup)))
        .call((g) => g.selectAll('line').remove());

      root
        .selectAll('.percentage-bar')
        .data(this.plotData, (item) => item.name)
        .enter()
        .append('rect')
        .attr('class', 'percentage-bar')
        .attr('x', 0)
        .attr('y', (item) => y(item.name))
        .attr('width', (item) => x(item.percentage))
        .attr('height', y.bandwidth())
        .attr('rx', 3)
        .attr('ry', 3)
        .attr('fill', (_item, index) => this.palette[index % this.palette.length])
        .attr('opacity', (item) => {
          if (!this.selectedGroups.length) return 1;
          return this.selectedGroups.includes(item.name) ? 1 : 0.35;
        })
        .style('cursor', 'pointer')
        .on('mouseover', (event, item) => this.showTooltip(event, item))
        .on('mousemove', (event) => this.moveTooltip(event))
        .on('mouseout', () => this.hideTooltip())
        .on('click', (_event, item) => this.onBarClick(item));

      root
        .selectAll('.percentage-label')
        .data(this.plotData, (item) => item.name)
        .enter()
        .append('text')
        .attr('class', 'percentage-label')
        .attr('x', (item) => Math.min(x(item.percentage) + 8, innerWidth - 2))
        .attr('y', (item) => (y(item.name) || 0) + (y.bandwidth() / 2))
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', (item) => (x(item.percentage) > innerWidth - 40 ? 'end' : 'start'))
        .attr('dx', (item) => (x(item.percentage) > innerWidth - 40 ? -4 : 0))
        .style('font-size', '12px')
        .style('fill', '#333333')
        .text((item) => `${item.percentage}%`);
    },

    formatAxisLabel(label, isPopup) {
      const maxLength = isPopup ? 42 : 24;

      if (label.length <= maxLength) {
        return label;
      }

      return `${label.slice(0, maxLength - 3)}...`;
    },

    showTooltip(event, item) {
      const tooltip = this.$refs.tooltip;
      if (!tooltip) return;

      tooltip.style.opacity = 1;
      tooltip.innerHTML = `Category: ${item.name}<br>Incidence: ${item.percentage}%`;
      this.moveTooltip(event);
    },

    moveTooltip(event) {
      const tooltip = this.$refs.tooltip;
      if (!tooltip) return;

      tooltip.style.left = `${event.clientX + 12}px`;
      tooltip.style.top = `${event.clientY + 12}px`;
    },

    hideTooltip() {
      const tooltip = this.$refs.tooltip;
      if (!tooltip) return;

      tooltip.style.opacity = 0;
    },

    onBarClick(item) {
      if (this.selectedGroups.includes(item.name)) {
        this.selectedGroups = this.selectedGroups.filter((key) => key !== item.name);
        this.filterarray = this.filterarray.filter((filter) => filter !== `(${item.name})`);
      } else {
        this.selectedGroups.push(item.name);
        this.filterarray.push(`(${item.name})`);
      }

      this.sortdata = this.plotData
        .filter((entry) => this.selectedGroups.includes(entry.name))
        .flatMap((entry) => entry.dataArray);

      this.filterkeyword = `${this.groupField}(${this.selectedGroups.join(',')})`;
      this.renderAllCharts();
    },

    cancelSelection() {
      this.selectedGroups = [];
      this.sortdata = [];
      this.filterarray = [];
      this.filterkeyword = '';
      this.renderAllCharts();
    },

    recreateChart() {
      if (!this.selectedGroups.length) return;

      const uniqueRows = [
        ...new Map(this.sortdata.map((row) => [JSON.stringify(row), row])).values(),
      ];

      if (!uniqueRows.length) return;

      this.$emit('drilldowncurrentchart', {
        sortedData: uniqueRows,
        filterkeyword: `${this.groupField}(${this.selectedGroups.join(',')})`,
      });

      this.cancelSelection();
    },

    handleExpandClose() {
      this.expandBlock = false;
    },

    showtable() {
      this.$emit('opendrillpopup', this.dataArray);
    },

    exportToExcel() {
      const excludedFields = ['id', 'subjectGuid'];
      const convertedData = this.dataArray.map(item => {
        const newItem = {};

        Object.keys(item).forEach(key => {
           if (excludedFields.includes(key)) {
            return;
          }
          const pascalKey = key
            .replace(/([A-Z])/g, ' $1')   // Add space before capitals
            .replace(/^./, str => str.toUpperCase()); // First letter uppercase

          newItem[pascalKey] = item[key];
        });

        return newItem;
      });

      const ws = XLSX.utils.json_to_sheet(convertedData);

      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      XLSX.writeFile(wb, `${this.chartLabel || 'chart'}.xlsx`);
    },
    //   const ws = XLSX.utils.json_to_sheet(this.dataArray);
    //   const wb = XLSX.utils.book_new();
    //   XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    //   XLSX.writeFile(wb, `${this.chartLabel || 'chart'}.xlsx`);
    // },

    exportToPdf() {
      const exclude = this.$refs.excludeThis;
      if (exclude) exclude.style.display = 'none';

      const wrap = document.createElement('div');
      wrap.appendChild(this.$refs.captureArea.cloneNode(true));
      wrap.style.cssText = 'width:100%;display:block;';

      html2pdf().set({
        margin: 0,
        filename: `${this.chartLabel || 'chart'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'pt', format: 'a4', orientation: 'landscape' },
      }).from(wrap).save();
    },
  },
};
</script>

<style scoped>
@import "../../custom_lib/AiCharts/charts.css";

.chart-container {
  width: 100%;
  min-height: 120px;
}

.chart-container svg {
  width: 100%;
  height: auto;
  display: block;
}

.popupconatiner {
  z-index: 10000;
}

.loader {
  border: 6px solid #f3f3f3;
  border-top: 6px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

.selectdrill img {
  width: 22px;
  height: 22px;
}

.chart-tooltip {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  opacity: 0;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
